import {
  AvatarIcon,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorIcon,
  ErrorListItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TransactionIcon,
} from '@razz-team/ui-web';
import { ApiAccount } from '../../../openapi';
import { useTranslation } from 'react-i18next';
import { useAccountTerminateMutation } from '../../../api/accounts';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';

export function TerminateAccountDialog({
  open,
  onClose,
  account,
}: {
  open: boolean;
  onClose: () => void;
  account: ApiAccount;
}) {
  const { t } = useTranslation();
  const idempotencyKey = useIdempotencyKey();
  const mutation = useAccountTerminateMutation({ idempotencyKey, accountId: account.id });
  const navigate = useNavigate();

  useEffect(() => {
    if (mutation.isSuccess) {
      navigate({ to: '/account/home', replace: true });
    }
  }, [mutation.isSuccess, navigate]);

  const handleCloseModal = useCallback(() => {
    onClose();
    mutation.reset();
  }, [onClose, mutation]);

  const name = account.name ?? account.balance.currency;

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleCloseModal}>
      <DialogTitle>
        {t(`Are you sure you want to terminate {{name}} account?`, { name })}
      </DialogTitle>
      <DialogContent>
        <List>
          <ListItem>
            <ListItemAvatar>
              <AvatarIcon icon={<TransactionIcon />} />
            </ListItemAvatar>
            <ListItemText
              primary={t('Move remaining money')}
              secondary={t('Convert, transfer or move any remaining money.')}
            />
          </ListItem>
          <ListItem>
            <ListItemAvatar>
              <AvatarIcon icon={<ErrorIcon />} />
            </ListItemAvatar>
            <ListItemText
              primary={t('You will lose access to this account')}
              secondary={t(
                'Your account details will stop working once you terminate {{ currency }} account.',
                { currency: account.balance.currency },
              )}
            />
          </ListItem>
          {mutation.error ? <ErrorListItem error={mutation.error} /> : null}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal} fullWidth>
          {t('Cancel')}
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          color="error"
          fullWidth
          loading={mutation.isPending}
        >
          {t('Close account')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
